import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, orderBy, doc, deleteDoc } from '@firebase/firestore';
import { Button, Modal, Row, Col, Card, Badge, Alert, Form } from 'react-bootstrap'; // Added Alert and Form
import { db } from '../../../firebase';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../components/context/AuthContext';
import { getStorage, ref as storageFBRef, getDownloadURL, deleteObject } from 'firebase/storage';
import { createLogger } from '../../../utils/logger';

const logger = createLogger('PaginatedProducts');

const ProductImage = ({ imagePath, altText, classNameIn, styleIn, onClick }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState(false);
  const storage = getStorage();

  useEffect(() => {
    setImageUrl(''); 
    setError(false);
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:') && !imagePath.startsWith('http')) {
      const imageRef = storageFBRef(storage, imagePath);
      getDownloadURL(imageRef)
        .then(setImageUrl)
        .catch(err => {
          logger.error(`[ProductImage Helper] Error fetching image URL for GCS path "${imagePath}":`, err);
          setError(true);
        });
    } else if (imagePath && typeof imagePath === 'string' && imagePath.startsWith('http')) {
      setImageUrl(imagePath);
    } else if (imagePath) {
      logger.warn("[ProductImage Helper] Invalid imagePath (e.g., blob or not a string):", imagePath);
      setError(true);
    }
  }, [imagePath, storage]);

  if (error) {
    return <div className={classNameIn || "card-img-top"} style={{...(styleIn || {height: '200px'}), backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}} onClick={onClick}><span>Image Error</span></div>;
  }
  if (!imageUrl) {
    return <div className={classNameIn || "card-img-top"} style={{...(styleIn || {height: '200px'}), backgroundColor: '#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}} onClick={onClick}><span>Loading...</span></div>;
  }
  return <img src={imageUrl} alt={altText} className={classNameIn || "card-img-top"} style={styleIn || {height: '200px', objectFit: 'cover'}} onClick={onClick} onError={() => setError(true)} />;
};

const PaginatedProducts = () => {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Store all products from DB
  const [initialLoading, setInitialLoading] = useState(true);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [errorPage, setErrorPage] = useState('');

  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState('');

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsProduct, setDetailsProduct] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const { userRole } = useAuth();
  const storage = getStorage();

  const PRODUCTS_COLLECTION = 'Products';

  // Load all products from database on mount
  useEffect(() => {
    const fetchAllProducts = async () => {
      setInitialLoading(true);
      setErrorPage('');
      try {
        // Fetch all products without pagination
        const productsQuery = query(
          collection(db, PRODUCTS_COLLECTION),
          orderBy('created_at', 'desc')
        );
        const querySnapshot = await getDocs(productsQuery);
        const fetchedProducts = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllProducts(fetchedProducts);
        setProducts(fetchedProducts);
      } catch (e) {
        setErrorPage('Failed to load products. Please try again.');
        logger.error("[PaginatedProducts] Error fetching all products:", e);
      }
      setInitialLoading(false);
    };
    fetchAllProducts();
  }, []); 

  const handleShowImages = (mediaArray, productName) => {
    setSelectedProductName(productName || 'Product');
    setSelectedMedia(mediaArray && Array.isArray(mediaArray) ? mediaArray : []);
    setShowImagesModal(true);
  };
  const handleCloseImages = () => setShowImagesModal(false);

  const openProductDetails = (product) => {
    setDetailsProduct(product);
    setShowDetailsModal(true);
  };
  const closeProductDetails = () => {
    setShowDetailsModal(false);
    setDetailsProduct(null);
  };

  const handleDeleteProduct = async (productId, productName, mediaToDelete) => {
    if (userRole !== 'Admin') {
      alert("You are not authorized to delete products.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete product: ${productName || productId}? This action cannot be undone.`)) {
      return;
    }
    setDeletingProductId(productId);
    setErrorPage('');
    try {
      const productDocRef = doc(db, PRODUCTS_COLLECTION, productId);
      await deleteDoc(productDocRef);
      if (mediaToDelete && Array.isArray(mediaToDelete) && mediaToDelete.length > 0) {
        for (const mediaItem of mediaToDelete) {
          if (mediaItem.path && typeof mediaItem.path === 'string') {
            try {
              const imageFileRef = storageFBRef(storage, mediaItem.path);
              await deleteObject(imageFileRef);
            } catch (storageError) {
              logger.error(`[PaginatedProducts] Failed to delete image ${mediaItem.path} from storage for product ${productId}:`, storageError);
              setErrorPage(prev => `${prev  } Note: Failed to delete image ${mediaItem.path} (${storageError.code}).`);
            }
          }
        }
      }
      setProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
      setAllProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
      alert('Product deleted successfully.');
    } catch (e) {
      setErrorPage(`Failed to delete product. ${  e.message}`);
      logger.error("[PaginatedProducts] Error deleting product:", productId, e);
    }
    setDeletingProductId(null);
  };

  // Unique categories from all products (for filter dropdown)
  const categories = useMemo(() => {
    const set = new Set();
    allProducts.forEach(p => set.add(p.category || 'Uncategorized'));
    return ['', ...Array.from(set).sort((a, b) => (a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b)))];
  }, [allProducts]);

  // Filter products by search term and category (client-side)
  useEffect(() => {
    const rawTerm = searchTerm.trim();
    const term = rawTerm.toLowerCase();
    const filtered = allProducts.filter(product => {
      const matchSearch = !rawTerm ||
        (product.display_name || '').toLowerCase().includes(term) ||
        (product.upc && product.upc.includes(rawTerm)) ||
        (product.description || '').toLowerCase().includes(term) ||
        (product.attributes && Array.isArray(product.attributes) &&
          product.attributes.some(a => `${a.name}: ${a.value}`.toLowerCase().includes(term)));
      const matchCategory = !categoryFilter || (product.category || 'Uncategorized') === categoryFilter;
      return matchSearch && matchCategory;
    });
    setProducts(filtered);
  }, [searchTerm, categoryFilter, allProducts]);

  const filteredProducts = products;

  // Group filtered products by category
  const productsByCategory = filteredProducts.reduce((acc, product) => {
    const category = product.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(product);
    return acc;
  }, {});

  // Sort categories: categorized first, 'Uncategorized' last
  const sortedCategoryEntries = Object.entries(productsByCategory).sort(([a], [b]) => {
    if (a === 'Uncategorized') return 1;
    if (b === 'Uncategorized') return -1;
    return a.localeCompare(b);
  });

  const formatDetailValue = (v) => {
    if (v === null || v === undefined) return '—';
    if (typeof v?.toDate === 'function') return v.toDate().toLocaleString();
    if (v instanceof Date) return v.toLocaleString();
    if (typeof v === 'object') return JSON.stringify(v, null, 2);
    return String(v);
  };

  return (
    <div>
      {/* Header with responsive layout */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4">
        <h1 className="mb-3 mb-sm-0">Products</h1>
        {userRole === 'Admin' && (
          <Link to="/admin/products/create" className="btn btn-primary">
            Create New Product
          </Link>
        )}
      </div>
      
      {/* Search Bar and Category Filter */}
      <Row className="mb-4 align-items-center g-2">
        <Col xs={12} md={6}>
          <div className="position-relative">
            <Form.Control
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-100"
              style={{ fontSize: '16px' }} // Prevents zoom on iOS
            />
            {searchTerm && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchTerm('')}
                style={{
                  fontSize: '16px',
                  minWidth: '44px',
                  position: 'absolute',
                  right: '0',
                  top: '0',
                  height: '100%',
                  borderTopLeftRadius: '0',
                  borderBottomLeftRadius: '0',
                  zIndex: 10
                }}
              >
                ×
              </Button>
            )}
          </div>
        </Col>
        <Col xs={12} md={4} lg={3}>
          <Form.Select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            aria-label="Filter by category"
            style={{ fontSize: '16px' }}
          >
            <option value="">All categories</option>
            {categories.filter(c => c !== '').map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {errorPage && <Alert variant="danger">{errorPage}</Alert>}
      {initialLoading && products.length === 0 && <p>Loading products...</p>}
      {!initialLoading && products.length === 0 && (
        <p>No products found{searchTerm || categoryFilter ? ' with the current search or category filter.' : '.'}</p>
      )}

      {(() => {
        // Flatten all products with their categories for space filling
        const allProducts = [];
        sortedCategoryEntries.forEach(([category, categoryProducts]) => {
          categoryProducts.forEach(product => {
            allProducts.push({ ...product, category });
          });
        });

        // Group products into rows of 4 (or 3 on medium screens, 2 on small, 1 on xs)
        const productsPerRow = 4;
        const productRows = [];
        for (let i = 0; i < allProducts.length; i += productsPerRow) {
          productRows.push(allProducts.slice(i, i + productsPerRow));
        }

        return (
          <div>
            {productRows.map((row, rowIndex) => (
              <Row key={rowIndex} xs={1} sm={2} md={3} lg={4} className="g-3 mb-4">
                {row.map(product => {
                  const editButtonDisabled = initialLoading || deletingProductId === product.id;
                  return (
                    <Col key={product.id}>
                      <Card className="h-100 shadow-sm">
                        {product.media && product.media.length > 0 && product.media[0] && product.media[0].url ? (
                          <ProductImage 
                              imagePath={product.media[0].url} 
                              altText={product.display_name || 'Product Image'} 
                              classNameIn="card-img-top" 
                              styleIn={{ height: '180px', objectFit: 'cover', cursor:'pointer' }}
                              onClick={() => handleShowImages(product.media, product.display_name)} 
                            />
                        ) : (
                          <div style={{ height: '180px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor:'pointer' }} onClick={() => handleShowImages(product.media, product.display_name)}>
                              <span className="small">{product.media && product.media.length > 0 ? 'View Images (URL missing)' : 'No Image'}</span>
                          </div>
                        )}
                        <Card.Body className="d-flex flex-column">
                          <div className="mb-2">
                            <Badge bg="secondary" className="text-white small me-2">
                              {product.category}
                            </Badge>
                            <Badge bg={product.status === 'active' ? 'success' : 'secondary'} className="small">
                              {product.status || 'N/A'}
                            </Badge>
                          </div>
                          <Card.Title className="text-break small">{product.display_name || 'N/A'}</Card.Title>
                          <Card.Text className="small text-muted mb-2">
                            UPC: {product.upc || '—'}
                          </Card.Text>
                          <Card.Text 
                            style={{ 
                              maxHeight: '60px', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              fontSize: '0.9rem'
                            }}
                            className="mb-auto"
                          >
                            {product.description || 'No description available.'}
                          </Card.Text>
                          {product.attributes && Array.isArray(product.attributes) && product.attributes.length > 0 && (
                            <div className="mt-2">
                              <h6 className="small mb-1">Attributes:</h6>
                              <div style={{fontSize: '0.8rem'}} className="text-muted">
                                {product.attributes.slice(0, 2).map((attr, idx) => (
                                  <div key={idx} className="mb-1">
                                    <strong>{attr.name}:</strong> {attr.value}
                                  </div>
                                ))}
                                {product.attributes.length > 2 && <div className="small">...</div>}
                              </div>
                            </div>
                          )}
                        </Card.Body>
                        <Card.Footer className="bg-transparent border-top-0 p-2">
                          <div className="d-flex flex-column gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                className="w-100"
                                onClick={() => openProductDetails(product)}
                                disabled={initialLoading}
                            >
                                View full details
                            </Button>
                            <Button
                                variant="outline-info"
                                size="sm"
                                className="w-100"
                                onClick={() => handleShowImages(product.media, product.display_name)}
                                disabled={!product.media || product.media.length === 0 || initialLoading}
                            >
                                View Images
                            </Button>
                            {userRole === 'Admin' && (
                              <div className="d-flex gap-1">
                                <Link 
                                  to={`/admin/products/edit/${product.id}`} 
                                  className={`btn btn-outline-warning btn-sm flex-fill ${editButtonDisabled ? 'disabled' : ''}`}
                                >
                                  Edit
                                </Link>
                                <Button 
                                    variant="outline-danger" 
                                    size="sm" 
                                    className="flex-fill"
                                    onClick={() => handleDeleteProduct(product.id, product.display_name, product.media)}
                                    disabled={editButtonDisabled} 
                                >
                                    {deletingProductId === product.id ? 'Deleting...' : 'Delete'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </Card.Footer>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            ))}
          </div>
        );
      })()}


      <Modal show={showImagesModal} onHide={handleCloseImages}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedProductName} - Images</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMedia.length === 0 ? (
            <p>No images available for this product.</p>
          ) : (
            <div className="d-flex flex-wrap">
              {selectedMedia.map((mediaItem, idx) => (
                  <ProductImage 
                    key={idx}
                    imagePath={mediaItem.url} 
                    altText={`${selectedProductName} Media ${idx + 1}`}
                    classNameIn="img-thumbnail me-2 mb-2"
                    styleIn={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'contain' }} 
                  />
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseImages}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal size="lg" show={showDetailsModal} onHide={closeProductDetails} scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {detailsProduct?.display_name || 'Product details'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailsProduct && (() => {
            const p = detailsProduct;
            const tags = Array.isArray(p.tags) ? p.tags : [];
            const shownKeys = new Set([
              'id', 'display_name', 'seo_name', 'upc', 'description', 'category', 'status', 'tags',
              'attributes', 'media', 'created_at', 'updated_at',
            ]);
            const extraEntries = Object.entries(p).filter(([k]) => !shownKeys.has(k));
            return (
              <>
                <dl className="row mb-0 small">
                  <dt className="col-sm-3 text-muted">Product ID</dt>
                  <dd className="col-sm-9 font-monospace text-break">{p.id}</dd>
                  <dt className="col-sm-3 text-muted">Display name</dt>
                  <dd className="col-sm-9">{p.display_name || '—'}</dd>
                  <dt className="col-sm-3 text-muted">SEO name</dt>
                  <dd className="col-sm-9 font-monospace text-break">{p.seo_name || '—'}</dd>
                  <dt className="col-sm-3 text-muted">UPC</dt>
                  <dd className="col-sm-9 font-monospace text-break">{p.upc || '—'}</dd>
                  <dt className="col-sm-3 text-muted">Category</dt>
                  <dd className="col-sm-9">{p.category || '—'}</dd>
                  <dt className="col-sm-3 text-muted">Status</dt>
                  <dd className="col-sm-9">
                    <Badge bg={p.status === 'active' ? 'success' : 'secondary'}>{p.status || '—'}</Badge>
                  </dd>
                  <dt className="col-sm-3 text-muted">Tags</dt>
                  <dd className="col-sm-9">{tags.length > 0 ? tags.join(', ') : '—'}</dd>
                  <dt className="col-sm-3 text-muted">Created</dt>
                  <dd className="col-sm-9">{formatDetailValue(p.created_at)}</dd>
                  <dt className="col-sm-3 text-muted">Updated</dt>
                  <dd className="col-sm-9">{formatDetailValue(p.updated_at)}</dd>
                </dl>
                <h6 className="mt-3 mb-2">Description</h6>
                <p className="small text-break mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {p.description || '—'}
                </p>
                <h6 className="mt-3 mb-2">Attributes</h6>
                {p.attributes && Array.isArray(p.attributes) && p.attributes.length > 0 ? (
                  <ul className="list-unstyled small mb-0">
                    {p.attributes.map((attr, idx) => (
                      <li key={idx} className="mb-1">
                        <strong>{attr.name || '—'}:</strong> {attr.value ?? '—'}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="small text-muted mb-0">—</p>
                )}
                <h6 className="mt-3 mb-2">Media</h6>
                {p.media && Array.isArray(p.media) && p.media.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {p.media.map((mediaItem, idx) => (
                      <div key={idx} className="text-center">
                        <ProductImage
                          imagePath={mediaItem.url}
                          altText={`${p.display_name || 'Product'} ${idx + 1}`}
                          classNameIn="img-thumbnail"
                          styleIn={{ maxWidth: '120px', maxHeight: '120px', objectFit: 'contain' }}
                        />
                        {mediaItem.path && (
                          <div className="text-muted text-break mt-1" style={{ maxWidth: '140px', fontSize: '0.7rem' }}>
                            {mediaItem.path}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="small text-muted mb-0">No media</p>
                )}
                {extraEntries.length > 0 && (
                  <>
                    <h6 className="mt-3 mb-2">Other fields</h6>
                    <pre className="small bg-light p-2 rounded mb-0 text-break" style={{ maxHeight: '200px', overflow: 'auto' }}>
                      {JSON.stringify(
                        Object.fromEntries(extraEntries),
                        null,
                        2
                      )}
                    </pre>
                  </>
                )}
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeProductDetails}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PaginatedProducts;
